import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { BusinessProvider } from './contexts/BusinessContext';
import { CartProvider } from './contexts/CartContext';
import { SiteContentProvider } from './contexts/SiteContentContext';
import { Toaster } from './components/ui/sonner';
import AgencyLanding from './pages/AgencyLanding';
import SolucionesPage from './pages/SolucionesPage';
import PlanesPage from './pages/PlanesPage';
import DemoPage from './pages/DemoPage';
import LoginPage from './pages/LoginPage';
import ClientView from './pages/ClientView';
import CustomDomainView from './pages/CustomDomainView';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import AdminPanel from './pages/AdminPanel';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Dominio principal de la plataforma
const MAIN_DOMAIN = 'mispedidosapp.com.ar';
// Hosts que nunca son dominios de cliente
const KNOWN_HOSTS = ['localhost', 'preview.emergentagent.com', 'emergent.host', 'vercel.app', 'netlify.app'];

// Extrae el slug si el hostname es un subdominio de mispedidosapp.com.ar
// Ej: "demo-burger.mispedidosapp.com.ar" → "demo-burger"
function getSubdomainSlug(hostname) {
  if (hostname.endsWith('.' + MAIN_DOMAIN)) {
    const sub = hostname.slice(0, -(MAIN_DOMAIN.length + 1));
    if (sub && sub !== 'www') return sub;
  }
  return null;
}

function App() {
  const [customSlug, setCustomSlug] = useState(null);
  const [domainChecking, setDomainChecking] = useState(false);

  useEffect(() => {
    const host = window.location.hostname;

    // Entorno de desarrollo / preview → routing normal
    if (KNOWN_HOSTS.some(h => host === h || host.includes(h))) return;
    // Dominio principal → routing normal
    if (host === MAIN_DOMAIN || host === 'www.' + MAIN_DOMAIN) return;

    // Subdominio de mispedidosapp.com.ar → slug directo, sin DB lookup
    const sub = getSubdomainSlug(host);
    if (sub) { setCustomSlug(sub); return; }

    // Dominio personalizado externo → consultar DB
    setDomainChecking(true);
    fetch(`${BACKEND_URL}/api/public/business/by-domain?domain=${host}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.slug) setCustomSlug(data.slug); })
      .catch(() => {})
      .finally(() => setDomainChecking(false));
  }, []);

  if (domainChecking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  // Dominio de cliente detectado → solo renderizar la tienda del negocio
  if (customSlug) {
    return (
      <SiteContentProvider>
        <BusinessProvider>
          <CartProvider>
            <div className="App">
              <BrowserRouter>
                <Routes>
                  <Route path="*" element={<CustomDomainView slug={customSlug} />} />
                </Routes>
              </BrowserRouter>
              <Toaster position="top-center" richColors />
            </div>
          </CartProvider>
        </BusinessProvider>
      </SiteContentProvider>
    );
  }

  // Routing normal de la plataforma (localhost, preview, o dominio principal sin negocio asignado)
  return (
    <AuthProvider>
      <SiteContentProvider>
        <BusinessProvider>
          <CartProvider>
            <div className="App">
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<AgencyLanding />} />
                  <Route path="/soluciones" element={<SolucionesPage />} />
                  <Route path="/planes" element={<PlanesPage />} />
                  <Route path="/demo" element={<DemoPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/superadmin" element={<SuperAdminDashboard />} />
                  <Route path="/admin" element={<AdminPanel />} />
                  <Route path="/:slug" element={<ClientView />} />
                  <Route path="/:slug/:step" element={<ClientView />} />
                </Routes>
              </BrowserRouter>
              <Toaster position="top-center" richColors />
            </div>
          </CartProvider>
        </BusinessProvider>
      </SiteContentProvider>
    </AuthProvider>
  );
}

export default App;
